/**
 * External API Adapter
 * Implements data ports using fetch to external API
 */

import { config } from "@/lib/config";
import type {
  ProductsPort,
  StockPort,
  BatchesPort,
  SalesPort,
  IngredientsPort,
  UsersPort,
  ReportsPort,
  EntradaPort,
  QueryOptions,
  ListResponse,
  CreateOptions,
} from "../ports";

const API_BASE = config.API_BASE_URL;

// Helper function for API calls
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Products Adapter
export const productsAdapter: ProductsPort = {
  async list(options?: QueryOptions) {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", options.page.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.search) params.set("search", options.search);
    
    return apiCall<ListResponse<any>>(`/products?${params}`);
  },

  async get(id: string) {
    return apiCall(`/products/${id}`);
  },

  async create(input: any, options?: CreateOptions) {
    const headers: Record<string, string> = {};
    if (options?.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }
    
    return apiCall(`/products`, {
      method: "POST",
      body: JSON.stringify(input),
      headers,
    });
  },

  async update(id: string, input: any) {
    return apiCall(`/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  async delete(id: string) {
    await apiCall(`/products/${id}`, { method: "DELETE" });
  },

  async getProdutoCustosUnitarios(produtoIds: string[]) {
    // Not implemented for external API yet
    return [];
  },
};

// Stock Adapter
export const stockAdapter: StockPort = {
  async current(filters?: any) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.set(key, String(value));
      });
    }
    
    return apiCall<any[]>(`/stock?${params}`);
  },

  async adjust(input) {
    return apiCall(`/stock/adjust`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
};

// Batches Adapter
export const batchesAdapter: BatchesPort = {
  async create(input: any, options?: CreateOptions) {
    const headers: Record<string, string> = {};
    if (options?.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }
    
    return apiCall(`/batches`, {
      method: "POST",
      body: JSON.stringify(input),
      headers,
    });
  },

  async list(options?: QueryOptions) {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", options.page.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    
    return apiCall<ListResponse<any>>(`/batches?${params}`);
  },

  async get(id: string) {
    return apiCall(`/batches/${id}`);
  },
};

// Sales Adapter
export const salesAdapter: SalesPort = {
  async create(input: any, options?: CreateOptions) {
    const headers: Record<string, string> = {};
    if (options?.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }
    
    return apiCall(`/sales`, {
      method: "POST",
      body: JSON.stringify(input),
      headers,
    });
  },

  async list(options?: QueryOptions) {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", options.page.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    
    return apiCall<ListResponse<any>>(`/sales?${params}`);
  },

  async get(id: string) {
    return apiCall(`/sales/${id}`);
  },
};

// Ingredients Adapter
export const ingredientsAdapter: IngredientsPort = {
  async list(options?: QueryOptions) {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", options.page.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    
    return apiCall<ListResponse<any>>(`/ingredients?${params}`);
  },

  async get(id: string) {
    return apiCall(`/ingredients/${id}`);
  },

  async create(input: any) {
    return apiCall(`/ingredients`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async update(id: string, input: any) {
    return apiCall(`/ingredients/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  async addStock(input) {
    return apiCall(`/ingredients/stock`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
};

// Users Adapter
export const usersAdapter: UsersPort = {
  async me() {
    return apiCall(`/users/me`);
  },

  async login(credentials) {
    return apiCall(`/auth/login`, {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  },

  async logout() {
    await apiCall(`/auth/logout`, { method: "POST" });
  },

  async list(options?: QueryOptions) {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", options.page.toString());
    if (options?.limit) params.set("limit", options.limit.toString());
    
    return apiCall<ListResponse<any>>(`/users?${params}`);
  },

  async create(input: any) {
    return apiCall(`/users`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async update(id: string, input: any) {
    return apiCall(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
};

// Reports Adapter
export const reportsAdapter: ReportsPort = {
  async sales(filters) {
    const params = new URLSearchParams();
    params.set("startDate", filters.startDate);
    params.set("endDate", filters.endDate);
    
    return apiCall(`/reports/sales?${params}`);
  },

  async production(filters) {
    const params = new URLSearchParams();
    params.set("startDate", filters.startDate);
    params.set("endDate", filters.endDate);
    
    return apiCall(`/reports/production?${params}`);
  },

  async stock() {
    return apiCall(`/reports/stock`);
  },
};

// Categories Adapter (stub)
const notImplemented = () => {
  throw new Error("Not implemented with external API");
};

export const categoriesAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const unitsAdapter = {
  list: notImplemented,
  get: notImplemented,
};

export const entradaAdapter: EntradaPort = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
  finalizar: notImplemented,
  createCabecalho: notImplemented,
  createItem: notImplemented,
  updateItem: notImplemented,
  deleteItem: notImplemented,
  getItens: notImplemented,
  atualizarTotalEntrada: notImplemented,
  finalizarEntrada: notImplemented,
};

export const estoqueAdapter = {
  list: notImplemented,
  get: notImplemented,
  getHistory: notImplemented,
  getExpiring: notImplemented,
};

export const tipoProdutosAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const estabelecimentosAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const clientesAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const vendasAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
  getSummary: notImplemented,
};

export const receitasAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const tipoMovimentoEntradaAdapter = {
  list: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const tipoDocumentoAdapter = {
  list: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const formaPagamentoAdapter = {
  list: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const categoriaReceitaAdapter = {
  list: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const nivelDificuldadeAdapter = {
  list: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const fornadasAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
  finalizar: notImplemented,
  cancelar: notImplemented,
  duplicar: notImplemented,
  getItens: notImplemented,
};

export const fornecedorAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};

export const marcaAdapter = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  delete: notImplemented,
};
